import type { IdType } from "@ifa/utils";

/**
 * Request，beefintech.ifa.order.domain.vo.OrderVO
 *
 * OrderVO
 */
export interface OrderVO {
  /**
   * 顾问名称
   */
  advisorName?: string;
  /**
   * 顾问团队名称
   */
  advisorTeamName?: string;
  /**
   * 顾问团队ID
   */
  advisorTid?: number;
  /**
   * 渠道信息
   */
  advisorUid?: number;
  /**
   * 受益人ID列表
   */
  beneficiaries?: BeneficiaryVO[];
  /**
   * 投受益人信息
   */
  customerId?: number;
  /**
   * 订单财务信息
   */
  financial?: OrderFinancialVO;
  /**
   * 产品信息
   */
  insurance?: string;
  /**
   * 保险公司名称
   */
  insuranceName?: string;
  /**
   * 受保人
   */
  insured?: InsuredDTO;
  /**
   * 受保人类型
   */
  insuredType?: Type;
  /**
   * 是否需要填寫財務資料
   */
  isFinancialInfoRequired?: boolean;
  /**
   * 订单审核状态
   */
  orderAuditStatus?: OrderAuditStatus;
  /**
   * 订单号
   */
  orderNo?: string;
  /**
   * 订单状态
   */
  orderStatus?: OrderStatus;
  /**
   * 投保人
   */
  policyholder?: PolicyholderVO;
  /**
   * 投保人类型
   */
  policyholderType?: Type;
  /**
   * 保单号
   */
  policyNo?: string;
  /**
   * 保单状态
   */
  policyStatus?: number;
  /**
   * 产品编码
   */
  productCode?: string;
  /**
   * 产品名称
   */
  productName?: string;
  /**
   * 产品计划列表
   */
  productPlans?: OrderProductPlanVO[];
  /**
   * 推荐人名称
   */
  referrerName?: string;
  /**
   * 推荐人团队名称
   */
  referrerTeamName?: string;
  /**
   * 推荐人团队ID
   */
  referrerTid?: number;
  /**
   * 推荐人ID
   */
  referrerUid?: number;
  /**
   * 合规审核状态
   */
  riskAssessmentAuditStatus?: RiskAssessmentAuditStatus;
  /**
   * 第二受保人
   */
  secondInsured?: InsuredDTO;
  /**
   * 第二受保人类型
   */
  secondInsuredType?: Type;
  /**
   * 第二投保人
   */
  secondPolicyholder?: PolicyholderVO;
  /**
   * 第二投保人类型
   */
  secondPolicyholderType?: Type;
  /**
   * 签名信息
   */
  signInfo?: OrderSignInfo;
  /**
   * 签名时间
   */
  signTime?: string;
  /**
   * 提交时间
   */
  submitTime?: string;
  productCompleted?: boolean;
  /**
   * 签名是否完成
   */
  signatureCompleted?: boolean;
  [property: string]: any;
}

/**
 * beefintech.ifa.base.basic.domain.vo.BeneficiaryVO
 *
 * BeneficiaryVO
 */
export interface BeneficiaryVO {
  basic?: BasicInfo;
  beneficiaryId?: number;
  relationship?: BeneficiaryRelationship;
  percentage?: number;
  remark?: string;
  [property: string]: any;
}

/**
 * BasicInfo
 */
export interface BasicInfo {
  /**
   * 出生日期
   */
  birthDate?: string;
  /**
   * 中文名字
   */
  chineseGivenName?: string;
  /**
   * 中文姓氏
   */
  chineseSurname?: string;
  /**
   * 英文名字
   */
  englishGivenName?: string;
  /**
   * 英文姓氏
   */
  englishSurname?: string;
  /**
   * 性别
   */
  gender?: Gender;
  /**
   * 婚姻状况
   */
  marriage?: MaritalStatus;
  /**
   * 身份证明文件号码
   */
  idNumber?: string;
  /**
   * 身份证明文件类型
   */
  idType?: IdType;
  /**
   * 受益人ID
   */
  sourceId?: number;
  [property: string]: any;
}

/**
 * 性别
 *
 * 称谓
 */
export enum Gender {
  Female = "FEMALE",
  Male = "MALE",
  Unknown = "UNKNOWN",
}

/**
 * 婚姻状况
 */
export enum MaritalStatus {
  Divorced = "DIVORCED",
  Married = "MARRIED",
  Single = "SINGLE",
  Unknown = "UNKNOWN",
  Widowed = "WIDOWED",
}

export enum BeneficiaryRelationship {
  Brother = "BROTHER",
  BrotherAndSister = "BROTHER_AND_SISTER",
  Child = "CHILD",
  Employee = "EMPLOYEE",
  Fiance = "FIANCE",
  Fiancee = "FIANCEE",
  GrandparentOrGrandchild = "GRANDPARENT_OR_GRANDCHILD",
  LegalGuardian = "LEGAL_GUARDIAN",
  Other = "OTHER",
  Parent = "PARENT",
  Self = "SELF",
  Sister = "SISTER",
  SisterAndBrother = "SISTER_AND_BROTHER",
  Spouse = "SPOUSE",
  Trustee = "TRUSTEE",
  Trustor = "TRUSTOR",
  Unidentified = "UNIDENTIFIED",
  Unknown = "UNKNOWN",
}

/**
 * 合规审核状态
 */
export enum ComplianceAuditStatus {
  Approved = "APPROVED",
  FirstApproved = "FIRST_APPROVED",
  FirstRejected = "FIRST_REJECTED",
  Initial = "INITIAL",
  Pending = "PENDING",
  PendingConfirmation = "PENDING_CONFIRMATION",
  PendingModification = "PENDING_MODIFICATION",
  Rejected = "REJECTED",
  SecondApproved = "SECOND_APPROVED",
  SecondRejected = "SECOND_REJECTED",
}

/**
 * 订单财务信息
 *
 * OrderFinancialVO
 */
export interface OrderFinancialVO {
  /**
   * 推荐的保險產品
   */
  chooseProducts?: ChooseProduct[];
  /**
   * 流动资产
   */
  currentAssets?: CurrentAssets;
  /**
   * 过去2年的平均每月支出
   */
  expense?: MonthlyExpense;
  /**
   * FNA
   */
  financialAnalysis?: FormData[];
  financialId?: number;
  /**
   * 是否需要填寫財務資料
   */
  isFinancialInfoRequired?: boolean;
  /**
   * 閣下是否有任何現有的保單被用作“保費融資”的抵押品或以其他方式質押或轉讓作為貸款工具的抵押品
   */
  hasExistingPolicyAsMortgage?: boolean;
  /**
   * 过去2年的平均每月收入
   */
  income?: MonthlyIncome;
  /**
   * 閣下是否打算運用“保費融資”安排此保單
   */
  isPremiumFinanced?: boolean;
  /**
   * 负债
   */
  liabilities?: Liabilities;
  /**
   * 非流动资产
   */
  nonCurrentAssets?: NonCurrentAssets;
  /**
   * 是否只能提供一款保险产品
   */
  onlyProvideOneProduct?: boolean;
  /**
   * 是否只能提供一家保險公司建議之原因
   */
  onlyProvideOneProductReason?: string;
  orderId?: number;
  /**
   * 現有保費融資的抵押保單詳情
   */
  premiumFinancing?: PremiumFinancing[];
  premiumFinanceAnalysis?: FormData[];
  /**
   * 现行保障
   */
  protection?: CurrentProtection;
  /**
   * 建议及原因
   */
  suggestionsAndReasons?: FormData[];
  /**
   * 转保声明
   */
  transferInsuranceDeclaration?: FormData[];
  [property: string]: any;
}

export interface ChooseProduct {
  choose?: boolean;
  productCode?: string;
  [property: string]: any;
}

export interface FormData {
  items?: FormDataItem[];
  question?: string;
  [property: string]: any;
}

export interface FormDataItem {
  key?: string;
  value?: any;
  [property: string]: any;
}

export interface PremiumFinancing {
  insuranceCompany?: string;
  lenderName?: string;
  loanAmount?: string;
  loanInterestRate?: string;
  loanTerm?: string;
  policyNo?: string;
  remark?: string;
  repaymentAmount?: string;
  [property: string]: any;
}

/**
 * 流动资产
 *
 * CurrentAssets
 */
export interface CurrentAssets {
  /**
   * 现金及存款
   */
  cashAndDeposits?: number;
  /**
   * 是否有其他流动资产
   */
  hasOtherCurrentAssets?: boolean;
  /**
   * 变现能力好的投资(股票/债券等)
   */
  liquidInvestments?: number;
  /**
   * 其他流动资产金额
   */
  otherCurrentAssetsAmount?: number;
  /**
   * 其他流动资产名称
   */
  otherCurrentAssetsName?: string;
  [property: string]: any;
}

/**
 * 过去2年的平均每月支出
 *
 * MonthlyExpense
 */
export interface MonthlyExpense {
  /**
   * 是否有其他支出
   */
  hasOtherExpense?: boolean;
  /**
   * 保险供款
   */
  insuranceContribution?: number;
  /**
   * 按揭/借贷/还款
   */
  mortgageLoanRepayment?: number;
  /**
   * 其他支出金额
   */
  otherExpenseAmount?: number;
  /**
   * 其他支出名称
   */
  otherExpenseName?: string;
  /**
   * 个人及家庭开支
   */
  personalAndFamilyExpense?: number;
  [property: string]: any;
}

/**
 * 过去2年的平均每月收入
 *
 * MonthlyIncome
 */
export interface MonthlyIncome {
  /**
   * 是否有其他收入
   */
  hasOtherIncome?: boolean;
  /**
   * 其他收入金额
   */
  otherIncomeAmount?: number;
  /**
   * 其他收入名称
   */
  otherIncomeName?: string;
  /**
   * 租金收入
   */
  rentalIncome?: number;
  /**
   * 工资/佣金/花紅
   */
  salaryCommissionBonus?: number;
  [property: string]: any;
}

/**
 * 负债
 *
 * Liabilities
 */
export interface Liabilities {
  /**
   * 是否有其他负债
   */
  hasOtherLiabilities?: boolean;
  /**
   * 按揭贷款
   */
  mortgageLoan?: number;
  /**
   * 其他债务金额
   */
  otherLiabilityAmount?: number;
  /**
   * 其他债务名称
   */
  otherLiabilityName?: string;
  /**
   * 私人/信用卡贷款
   */
  personalCreditCardLoan?: number;
  [property: string]: any;
}

/**
 * 非流动资产
 *
 * NonCurrentAssets
 */
export interface NonCurrentAssets {
  /**
   * 其他非流动资产(如公司股份)
   */
  otherNonCurrentAssets?: number;
  /**
   * 物业
   */
  property?: number;
  [property: string]: any;
}

/**
 * 现行保障
 *
 * CurrentProtection
 */
export interface CurrentProtection {
  /**
   * 意外保险
   */
  accidentInsurance?: string;
  /**
   * 附件资料/备注
   */
  attachmentRemarks?: string;
  /**
   * 危疾保险
   */
  criticalIllnessInsurance?: string;
  /**
   * 人寿保险
   */
  lifeInsurance?: string;
  /**
   * 医疗保险金额
   */
  medicalInsuranceAmount?: number;
  /**
   * 医疗保险类别
   */
  medicalInsuranceType?: string;
  /**
   * 其他现有保障
   */
  otherExistingProtection?: string;
  [property: string]: any;
}

/**
 * 受保人
 *
 * InsuredDTO
 *
 * 第二受保人
 */
export interface InsuredDTO {
  basic?: BasicInfo;
  insuredId?: number;
  personal?: PersonalInfo;
  relationship?: BeneficiaryRelationship;
  tax?: TaxInfo;
  [property: string]: any;
}

/**
 * PersonalInfo
 */
export interface PersonalInfo {
  /**
   * 通讯地址
   */
  address?: string;
  /**
   * 通讯地址国家
   */
  country?: string;
  /**
   * 电子邮箱
   */
  email?: string;
  /**
   * 身高
   */
  height?: number;
  /**
   * 手机号码
   */
  phone?: string;
  /**
   * 称谓
   */
  salutation?: Gender;
  /**
   * 吸烟量
   */
  smokingQuantity?: number;
  /**
   * 体重
   */
  weight?: number;
  /**
   * 住宅地址-國家/地區
   */
  residentialAddressRegion?: string;
  /**
   * 完整住宅地址
   */
  residentialAddress?: string;
  marriage?: MaritalStatus;
  [property: string]: any;
}

/**
 * TaxInfo
 */
export interface TaxInfo {
  countryOfBirth?: string;
  countryOfResidence?: string;
  hasHKIDCard?: boolean;
  hasOtherTaxResidency: boolean;
  hkEntryIdNumber?: string;
  hkEntryIdType?: HkEntryIdType;
  isHKPermanentResident?: boolean;
  isUSCitizen?: boolean;
  nationality?: string;
  remark?: string;
  [property: string]: any;
}

export enum HkEntryIdType {
  Passport = "PASSPORT",
  TempPassport = "TEMP_PASSPORT",
  Unknown = "UNKNOWN",
}

/**
 * 受保人类型
 *
 * 投保人类型
 *
 * 第二受保人类型
 *
 * 第二投保人类型
 */
export enum Type {
  Company = "COMPANY",
  Individual = "INDIVIDUAL",
}

/**
 * 订单审核状态
 */
export enum OrderAuditStatus {
  Approved = "APPROVED",
  Initial = "INITIAL",
  Pending = "PENDING",
  PendingConfirmation = "PENDING_CONFIRMATION",
  PendingModification = "PENDING_MODIFICATION",
  PendingRiskEvaluation = "PENDING_RISK_EVALUATION",
  Rejected = "REJECTED",
}

/**
 * 订单状态
 */
export enum OrderStatus {
  Completed = "COMPLETED",
  Draft = "DRAFT",
  Expired = "EXPIRED",
  PendingConfirmation = "PENDING_CONFIRMATION",
  PendingReview = "PENDING_REVIEW",
  PendingSignature = "PENDING_SIGNATURE",
}

/**
 * 投保人
 *
 * PolicyholderVO
 *
 * 第二投保人
 */
export interface PolicyholderVO {
  basic?: BasicInfo;
  familyMembers?: FamilyMember[];
  policyholderId?: number;
  isVulnerableCustomer?: boolean;
  personal?: PersonalInfo;
  tax?: TaxInfo;
  work?: WorkInfo;
  relationship?: BeneficiaryRelationship;
  [property: string]: any;
}

/**
 * beefintech.ifa.base.domain.info.FamilyMember
 *
 * FamilyMember
 */
export interface FamilyMember {
  age?: number;
  /**
   * 是否需要供养
   */
  isDependent?: boolean;
  relationship?: FamilyMemberRelationship;
  [property: string]: any;
}

export enum FamilyMemberRelationship {
  Child = "CHILD",
  Father = "FATHER",
  Mother = "MOTHER",
  Spouse = "SPOUSE",
  Unknown = "UNKNOWN",
}

/**
 * WorkInfo
 */
export interface WorkInfo {
  /**
   * 雇主信息
   */
  employer?: Employer;
  /**
   * title
   */
  jobTitle?: string;
  /**
   * 退休年龄
   */
  retirementAge?: string;
  /**
   * 預期退休年齡
   */
  intendedRetirementAge?: string;
  /**
   * 預期退休年齡其他说明
   */
  intendedRetirementAgeOther?: string;
  /**
   * 就业类型
   */
  type?: TypeEnum;
  [property: string]: any;
}

/**
 * 雇主信息
 *
 * Employer
 */
export interface Employer {
  address?: string;
  industry?: string;
  name?: string;
  [property: string]: any;
}

/**
 * 就业类型
 */
export enum TypeEnum {
  FullTime = "FULL_TIME",
  PartTime = "PART_TIME",
  SelfEmployed = "SELF_EMPLOYED",
  Unknown = "UNKNOWN",
  UnpaidPerson = "UNPAID_PERSON",
}

/**
 * beefintech.ifa.order.domain.vo.OrderProductPlanVO
 *
 * OrderProductPlanVO
 */
export interface OrderProductPlanVO {
  /**
   * 申请客户特殊首年保费折扣
   */
  applySpecialFirstYearDiscount?: boolean;
  /**
   * 公司码
   */
  companyCode?: string;
  /**
   * 公司名称
   */
  companyName?: string;
  /**
   * 供款年限
   */
  contributionYears?: string;
  /**
   * 币种
   */
  currency?: string;
  /**
   * 红利分派方式
   */
  dividendDistributionMethod?: string;
  /**
   * 首期缴费方式
   */
  firstPaymentMethod?: string;
  /**
   * 首期折扣后保费
   */
  firstYearDiscountedPremium?: number;
  /**
   * 是否支持直接付款授权(DDA)
   */
  isDdaSupported?: boolean;
  /**
   * 是否参加递增保障权益
   */
  isIncreasingProtectionIncluded?: boolean;
  /**
   * 是否保单回溯
   */
  isPolicyBackdated?: boolean;
  /**
   * 是否需要签署错配声明
   */
  isMismatchDeclarationRequired?: boolean;
  /**
   * 错配原因
   */
  mismatchReason?: string;
  /**
   * 是否保费融资
   */
  isPremiumFinanced?: boolean;
  /**
   * 是否预缴
   */
  isPrepaid?: boolean;
  /**
   * 缴费频率
   */
  paymentFrequency?: string;
  /**
   * 保单语言
   */
  policyLanguage?: string;
  /**
   * 每期保费
   */
  premiumPerPeriod?: number;
  /**
   * 预缴额(含首期)
   */
  prepaidAmountInclFirst?: number;
  /**
   * 产品码
   */
  productCode?: string;
  /**
   * 产品名称
   */
  productName?: string;
  /**
   * 保障期
   */
  protectionPeriod?: string;
  /**
   * 续期缴费方式
   */
  renewalPaymentMethod?: string;
  /**
   * 附加险
   */
  riders?: RiderVO[];
  /**
   * 保额
   */
  sumAssured?: number;
  [property: string]: any;
}

/**
 * beefintech.ifa.order.domain.vo.RiderVO
 *
 * RiderVO
 */
export interface RiderVO {
  /**
   * 供款年限
   */
  contributionYears?: string;
  /**
   * 币种
   */
  currency?: string;
  /**
   * 缴费频率
   */
  paymentFrequency?: string;
  /**
   * 每期保费
   */
  premiumPerPeriod?: number;
  /**
   * 附加险产品码
   */
  productCode?: string;
  /**
   * 附加险产品名称
   */
  productName?: string;
  /**
   * 保障期
   */
  protectionPeriod?: string;
  /**
   * 保额
   */
  sumAssured?: number;
  [property: string]: any;
}

/**
 * 签名信息
 *
 * OrderSignInfo
 */
export interface OrderSignInfo {
  advisorSignData?: string;
  city?: City;
  photo?: string;
  policyholderSignData?: string;
  [property: string]: any;
}

export enum City {
  HongKong = "HongKong",
}

/**
 * beefintech.ifa.order.domain.attachment.OrderAttachmentConfig
 *
 * OrderAttachmentConfig
 */
export interface OrderAttachmentConfig {
  /**
   * 附件上传条件
   */
  condition?: string;
  /**
   * 附件项列表
   */
  items?: AttachmentItem[];
  /**
   * 附件名称 (Category Name)
   */
  name?: string;
  /**
   * 唯一标识 (Key)
   */
  key?: string;
  /**
   * 是否必须
   */
  required?: boolean;
  /**
   * 附件类型
   */
  type?: string;
  [property: string]: any;
}

export type ComplianceAuditDTO = {
  complianceAuditStatus?: ComplianceAuditStatus;
  orderId?: number;
  reason?: string;
  [property: string]: any;
};

/**
 * beefintech.ifa.order.domain.attachment.OrderAttachmentConfig.AttachmentItem
 *
 * AttachmentItem
 */
export interface AttachmentItem {
  /**
   * 附件项文件类型列表(返回给前端的时候需要)
   */
  files?: any[]; // Changed from string[] to any[] to hold UploadFile objects in frontend state
  /**
   * 最大上传数量
   */
  max?: number;
  /**
   * 附件项名称
   */
  name?: string;
  /**
   * 是否必须
   */
  required?: boolean;
  /**
   * 附件项类型
   */
  type?: string;
  [property: string]: any;
}
